import { ChatMarkdown, RemoteImagesContext } from "@rakazo/chat-ui/web";
import { botChatAccent, darkTokens } from "@rakazo/ui-tokens";
import { Button, Input, Textarea } from "@rakazo/ui-web";
import { useEffect, useRef, useState } from "react";
import { TypingDots } from "../components/ai/primitives";
import { SharedBotHead } from "../components/SharedBotAppearance";
import { appName } from "../lib/branding";

type Message = { role: "user" | "assistant"; content: string };
export function SharedChatPage() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [access, setAccess] = useState("");
  const [password, setPassword] = useState("");
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);
  const [info, setInfo] = useState<{
    name: string;
    color?: string | null;
    allowedRepos: string[];
  }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const conversation = useRef<HTMLElement>(null);
  const following = useRef(true);
  const active = useRef<AbortController | null>(null);
  useEffect(() => {
    document.title = info?.name ? `${appName} - ${info.name}` : appName;
    return () => {
      document.title = appName;
    };
  }, [info?.name]);
  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/shared/info", {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, access }),
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.status === 401 && data.passwordRequired) {
          setPasswordRequired(true);
          setInfo(undefined);
          return;
        }
        if (!response.ok)
          throw new Error(data.error || "This link is disabled or no longer available.");
        setPasswordRequired(false);
        setInfo(data);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.message);
      });
    return () => {
      controller.abort();
      active.current?.abort();
    };
  }, [token, access, authVersion]);
  useEffect(() => {
    // Scroll only the transcript, never its ancestors or the page viewport.
    const pane = conversation.current;
    if (pane && following.current) pane.scrollTop = pane.scrollHeight;
  }, [messages, status]);
  async function unlock() {
    if (unlocking || !password) return;
    setUnlocking(true);
    setError("");
    try {
      const response = await fetch("/api/shared/unlock", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not unlock this bot.");
      setPassword("");
      setAccess(data.access);
      setAuthVersion((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not unlock this bot.");
    } finally {
      setUnlocking(false);
    }
  }
  async function send() {
    if (!input.trim() || busy || !info) return;
    const question = input.trim();
    const previous = messages;
    following.current = true;
    setInput("");
    setBusy(true);
    setError("");
    setStatus("On it…");
    const next: Message[] = [...previous, { role: "user", content: question }];
    setMessages([...next, { role: "assistant", content: "" }]);
    let answer = "";
    let completed = false;
    const controller = new AbortController();
    active.current = controller;
    try {
      const history: Message[] = [];
      let size = 0;
      for (const message of [...previous].reverse()) {
        if (history.length >= 20 || size + message.content.length > 60000) break;
        history.unshift({ ...message, content: message.content.slice(0, 16000) });
        size += message.content.length;
      }
      const response = await fetch("/api/shared/chat", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, access, message: question, history }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401 && data.passwordRequired) {
          setPasswordRequired(true);
          setInfo(undefined);
          setAccess("");
        }
        throw new Error(data.error || "Could not send message.");
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error("Connection unavailable.");
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let split = buffer.indexOf("\n");
          while (split !== -1) {
            const line = buffer.slice(0, split);
            buffer = buffer.slice(split + 1);
            split = buffer.indexOf("\n");
            if (!line) continue;
            const event = JSON.parse(line);
            if (event.type === "status") setStatus(event.text);
            if (event.type === "reset") answer = "";
            if (event.type === "text") {
              answer += event.text;
              setStatus("");
            }
            if (event.type === "reset" || event.type === "text")
              setMessages([...next, { role: "assistant", content: answer }]);
            if (event.type === "error") throw new Error(event.text);
            if (event.type === "done") completed = true;
          }
        }
      } finally {
        reader.releaseLock();
      }
      if (!completed) throw new Error("Connection interrupted. Please try again.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish the answer.");
      // A failed partial answer must not silently become future model context.
      setMessages(previous);
      setInput(question);
    } finally {
      setBusy(false);
      setStatus("");
      active.current = null;
    }
  }
  if (passwordRequired)
    return (
      <main className="grid h-dvh place-items-center bg-background p-6 text-foreground">
        <form
          className="w-full max-w-sm space-y-5 rounded-2xl border border-border p-6"
          onSubmit={(e) => {
            e.preventDefault();
            void unlock();
          }}
        >
          <div className="flex items-center gap-3">
            <SharedBotHead />
            <span className="font-medium">{appName}</span>
          </div>
          <h1 className="text-xl font-semibold">Password required</h1>
          <p className="text-sm text-muted-foreground">
            Enter the password shared by the bot owner. No account needed.
          </p>
          <label htmlFor="bot-password" className="block text-sm">
            Password
            <Input
              id="bot-password"
              className="mt-2"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={128}
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button className="w-full" type="submit" disabled={unlocking || !password}>
            {unlocking ? "Unlocking…" : "Open chat"}
          </Button>
        </form>
      </main>
    );
  return (
    <main className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <header className="shrink-0 border-b border-border px-5 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div className="flex items-center gap-3">
            <SharedBotHead color={info?.color} className="size-12" />
            <div>
              <h1 className="font-semibold">{info?.name ?? "Shared bot"}</h1>
              <p className="text-xs text-muted-foreground">{appName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              following.current = true;
              setMessages([]);
              setError("");
            }}
          >
            New chat
          </Button>
        </div>
      </header>
      <section
        ref={conversation}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-6"
        aria-label="Conversation"
        onScroll={(event) => {
          const pane = event.currentTarget;
          following.current = pane.scrollHeight - pane.clientHeight - pane.scrollTop < 80;
        }}
      >
        <div className="mx-auto max-w-3xl space-y-6">
          {!messages.length && info && (
            <p className="py-8 text-muted-foreground">
              Ask a question
              {info.allowedRepos.length ? ` about ${info.allowedRepos.join(", ")}` : ""}.
            </p>
          )}
          {messages.map((message, i) => (
            <article
              key={`${i}-${message.role}`}
              style={
                message.role === "user" && info?.color
                  ? {
                      backgroundColor: botChatAccent(info.color),
                      color: darkTokens.chatUserForeground,
                    }
                  : undefined
              }
              className={
                message.role === "user"
                  ? "relative ml-auto max-w-[85%] rounded-2xl bg-muted p-4"
                  : "relative flex items-start gap-3 break-words leading-relaxed"
              }
            >
              <span className="sr-only">{message.role === "user" ? "You" : info?.name}</span>
              {message.role === "assistant" ? (
                <>
                  <SharedBotHead color={info?.color} className="mt-1 size-9" />
                  <div
                    className="min-w-0 rounded-2xl border border-border bg-muted/40 px-4 py-3"
                    style={
                      info?.color
                        ? {
                            backgroundColor: `color-mix(in srgb, ${botChatAccent(info.color)} 18%, transparent)`,
                          }
                        : undefined
                    }
                  >
                    {busy && i === messages.length - 1 && !message.content.trim() ? (
                      <TypingDots />
                    ) : (
                      <RemoteImagesContext.Provider value={false}>
                        <ChatMarkdown
                          streaming={busy && i === messages.length - 1}
                          showCursor={false}
                        >
                          {message.content}
                        </ChatMarkdown>
                      </RemoteImagesContext.Provider>
                    )}
                  </div>
                </>
              ) : (
                message.content
              )}
            </article>
          ))}
          {status && (
            <p role="status" className="text-sm text-muted-foreground animate-pulse">
              {status}
            </p>
          )}
          {error && (
            <p role="alert" className="rounded-xl bg-muted p-4 text-destructive">
              {error}
            </p>
          )}
        </div>
      </section>
      <form
        className="shrink-0 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="mx-auto flex max-w-3xl items-end gap-3">
          <Textarea
            className="max-h-40 resize-none"
            aria-label="Message"
            placeholder="Ask a question…"
            value={input}
            maxLength={8000}
            rows={2}
            disabled={!info}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button disabled={busy || !input.trim() || !info} type="submit">
            {busy ? "Working…" : "Send"}
          </Button>
        </div>
      </form>
    </main>
  );
}
